/** Which screen is up, as a pure reducer. The DOM layer renders `screen`; nothing else decides navigation. */
export type ScreenId =
  | 'loading' | 'start' | 'levels' | 'drills' | 'garage' | 'intro' | 'play' | 'pause' | 'settings' | 'results' | 'credits';

export type UiEvent =
  | { type: 'loaded'; to?: ScreenId }
  | { type: 'go'; to: ScreenId }
  | { type: 'back' }
  | { type: 'pause' }
  | { type: 'resume' }
  | { type: 'finish' }
  | { type: 'restart' }
  | { type: 'help'; open: boolean };

export interface UiState { screen: ScreenId; stack: ScreenId[]; help: boolean }

export const initialUi = (): UiState => ({ screen: 'loading', stack: [], help: false });

/** Screens you can leave with Back/Esc/B. Play leaves through pause, loading not at all. */
export const canGoBack = (s: UiState) => s.stack.length > 0 && s.screen !== 'play' && s.screen !== 'loading';

export function reduce(s: UiState, e: UiEvent): UiState {
  if (e.type === 'help') return { ...s, help: e.open };
  if (s.screen === 'loading') {
    if (e.type !== 'loaded') return s;
    const to = e.to ?? 'start';
    return { screen: to, stack: to === 'start' ? [] : to === 'intro' || to === 'drills' ? ['start', 'levels'] : ['start'], help: false };
  }
  switch (e.type) {
    case 'loaded': return s;
    case 'go':
      if (e.to === s.screen || e.to === 'loading') return s;
      if (e.to === 'start') return { screen: 'start', stack: [], help: false };
      // Entering play drops intro from the trail: pause and finish decide where you go from there.
      if (e.to === 'play') return { screen: 'play', stack: s.stack.filter((x) => x !== 'play'), help: false };
      {
        // Leaving a ride (pause -> menus) drops play and pause from the trail; going to a screen already in the
        // trail returns to it instead of stacking a loop.
        const trail = s.screen === 'pause' && e.to !== 'settings' ? s.stack.filter((x) => x !== 'play') : [...s.stack, s.screen];
        const at = trail.indexOf(e.to);
        return at >= 0 ? { screen: e.to, stack: trail.slice(0, at), help: false } : { screen: e.to, stack: trail, help: false };
      }
    case 'back': {
      if (!canGoBack(s)) return s;
      return { screen: s.stack[s.stack.length - 1]!, stack: s.stack.slice(0, -1), help: false };
    }
    case 'pause': return s.screen === 'play' ? { screen: 'pause', stack: [...s.stack, 'play'], help: false } : s;
    case 'resume':
      return s.screen === 'pause' ? { screen: 'play', stack: s.stack.filter((x) => x !== 'play'), help: false } : s;
    case 'finish': return s.screen === 'play' ? { screen: 'results', stack: ['start', 'levels'], help: false } : s;
    case 'restart':
      return s.screen === 'pause' || s.screen === 'results' || s.screen === 'play'
        ? { screen: 'intro', stack: s.stack.filter((x) => x !== 'play' && x !== 'intro' && x !== 'pause'), help: false } : s;
  }
}
